const keObserveLayout = (elements, callback) => {
  let frame = null;
  const queue = () => {
    if (frame !== null) return;
    frame = window.requestAnimationFrame(() => {
      frame = null;
      callback();
    });
  };

  queue();

  if ('ResizeObserver' in window) {
    const observer = new ResizeObserver(queue);
    elements.forEach((element) => observer.observe(element));
    return () => {
      observer.disconnect();
      if (frame !== null) window.cancelAnimationFrame(frame);
    };
  }

  window.addEventListener('resize', queue);
  return () => {
    window.removeEventListener('resize', queue);
    if (frame !== null) window.cancelAnimationFrame(frame);
  };
};

class KabluchkaEditorialHero extends HTMLElement {
  constructor() {
    super();
    this.activeIndex = 0;
    this.timer = null;
    this.slides = [];
    this.dots = [];
    this.previousFocus = null;
    this.scrollPosition = 0;
    this.bodyTop = '';
    this.ownsScrollLock = false;
  }

  connectedCallback() {
    if (this.dataset.initialized === 'true') return;
    this.dataset.initialized = 'true';
    this.slides = Array.from(this.querySelectorAll('[data-ke-slide]'));
    this.dots = Array.from(this.querySelectorAll('[data-ke-dot]'));
    this.menuToggle = this.querySelector('[data-ke-menu-toggle]');
    this.mobileMenu = this.querySelector('[data-ke-mobile-menu]');
    this.scrollButton = this.querySelector('[data-ke-scroll]');
    this.mobileMenu?.setAttribute('aria-hidden', 'true');
    this.mobileMenu?.setAttribute('inert', '');

    this.dots.forEach((dot, index) => {
      dot.addEventListener('click', () => this.select(index, true));
    });

    this.menuToggle?.addEventListener('click', () => this.toggleMenu());
    this.scrollButton?.addEventListener('click', () => {
      this.closest('.shopify-section')?.nextElementSibling?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    });
    this.mobileMenu?.querySelectorAll('a').forEach((link) => {
      link.addEventListener('click', () => this.closeMenu({ restoreFocus: false }));
    });

    this.addEventListener('keydown', (event) => {
      if (event.key === 'ArrowRight') this.select(this.activeIndex + 1, true);
      if (event.key === 'ArrowLeft') this.select(this.activeIndex - 1, true);
      if (event.key === 'Escape' && this.isMenuOpen()) {
        event.preventDefault();
        this.closeMenu();
      }
      if (event.key === 'Tab' && this.isMenuOpen()) this.trapMenuFocus(event);
    });

    this.addEventListener('mouseenter', () => this.stop());
    this.addEventListener('mouseleave', () => this.start());
    this.addEventListener('focusin', () => this.stop());
    this.addEventListener('focusout', () => this.start());

    const initialIndex = this.slides.findIndex((slide) => slide.classList.contains('is-active'));
    this.select(initialIndex >= 0 ? initialIndex : 0);
    this.cleanupLayout = keObserveLayout(
      [this, ...this.querySelectorAll('.ke-hero__content, .ke-hero__cta')],
      () => this.updateLayout()
    );
    this.start();
  }

  disconnectedCallback() {
    this.closeMenu({ restoreFocus: false });
    this.stop();
    this.cleanupLayout?.();
  }

  updateLayout() {
    const heroRect = this.getBoundingClientRect();
    const headerRect = this.querySelector('.ke-hero__topbar')?.getBoundingClientRect();
    const safeTop = Math.max(heroRect.top + 24, (headerRect?.bottom || heroRect.top) + 24);
    const minimumGap = 24;

    this.slides.forEach((slide) => {
      const content = slide.querySelector('.ke-hero__content');
      const cta = slide.querySelector('.ke-hero__cta');
      if (!content || !cta) return;

      content.style.setProperty('--ke-content-shift-y', '0px');
      const contentRect = content.getBoundingClientRect();
      const ctaRect = cta.getBoundingClientRect();
      const requiredShift = Math.max(0, contentRect.bottom + minimumGap - ctaRect.top);
      const availableShift = Math.max(0, contentRect.top - safeTop);
      const shift = Math.min(requiredShift, availableShift);
      content.style.setProperty('--ke-content-shift-y', `${shift.toFixed(2)}px`);
    });
  }

  select(index, restart = false) {
    if (!this.slides.length) return;
    const nextIndex = (index + this.slides.length) % this.slides.length;
    this.activeIndex = nextIndex;

    this.slides.forEach((slide, slideIndex) => {
      const active = slideIndex === nextIndex;
      slide.classList.toggle('is-active', active);
      slide.setAttribute('aria-hidden', active ? 'false' : 'true');
      slide.querySelectorAll('a, button').forEach((element) => {
        element.tabIndex = active ? 0 : -1;
      });
    });

    this.dots.forEach((dot, dotIndex) => {
      dot.setAttribute('aria-current', dotIndex === nextIndex ? 'true' : 'false');
    });

    if (restart) {
      this.stop();
      this.start();
    }
  }

  start() {
    const delay = Number(this.dataset.autoplaySeconds || 0) * 1000;
    if (delay < 3000 || this.slides.length < 2 || window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;
    this.stop();
    this.timer = window.setInterval(() => this.select(this.activeIndex + 1), delay);
  }

  stop() {
    if (!this.timer) return;
    window.clearInterval(this.timer);
    this.timer = null;
  }

  toggleMenu() {
    if (this.isMenuOpen()) {
      this.closeMenu();
      return;
    }

    this.openMenu();
  }

  isMenuOpen() {
    return this.classList.contains('is-menu-open');
  }

  openMenu() {
    if (!this.menuToggle || !this.mobileMenu || this.isMenuOpen()) return;

    this.previousFocus = document.activeElement;
    this.classList.add('is-menu-open');
    this.menuToggle.setAttribute('aria-expanded', 'true');
    this.mobileMenu.setAttribute('aria-hidden', 'false');
    this.mobileMenu.removeAttribute('inert');
    this.lockPageScroll();
    this.mobileMenu.querySelector('a')?.focus();
  }

  closeMenu({ restoreFocus = true } = {}) {
    const wasOpen = this.isMenuOpen();
    this.classList.remove('is-menu-open');
    this.menuToggle?.setAttribute('aria-expanded', 'false');
    this.mobileMenu?.setAttribute('aria-hidden', 'true');
    this.mobileMenu?.setAttribute('inert', '');
    this.unlockPageScroll();

    if (wasOpen && restoreFocus) {
      const focusTarget = this.previousFocus?.isConnected ? this.previousFocus : this.menuToggle;
      focusTarget?.focus();
    }

    this.previousFocus = null;
  }

  trapMenuFocus(event) {
    const focusableElements = [
      this.menuToggle,
      ...Array.from(this.mobileMenu?.querySelectorAll('a, button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])') || [])
    ].filter((element) => element && !element.hasAttribute('disabled') && element.getAttribute('aria-hidden') !== 'true');

    if (!focusableElements.length) return;

    const firstElement = focusableElements[0];
    const lastElement = focusableElements[focusableElements.length - 1];

    if (event.shiftKey && document.activeElement === firstElement) {
      event.preventDefault();
      lastElement.focus();
    } else if (!event.shiftKey && document.activeElement === lastElement) {
      event.preventDefault();
      firstElement.focus();
    }
  }

  lockPageScroll() {
    if (document.documentElement.classList.contains('ke-menu-lock')) return;

    this.scrollPosition = window.scrollY;
    this.bodyTop = document.body.style.top;
    this.ownsScrollLock = true;
    document.documentElement.classList.add('ke-menu-lock');
    document.body.classList.add('ke-menu-lock');
    document.body.style.top = `-${this.scrollPosition}px`;
  }

  unlockPageScroll() {
    if (!this.ownsScrollLock) return;

    document.documentElement.classList.remove('ke-menu-lock');
    document.body.classList.remove('ke-menu-lock');
    document.body.style.top = this.bodyTop;
    window.scrollTo(0, this.scrollPosition);
    this.ownsScrollLock = false;
  }
}

if (!customElements.get('kabluchka-editorial-hero')) {
  customElements.define('kabluchka-editorial-hero', KabluchkaEditorialHero);
}

class KabluchkaEditorialDiscovery extends HTMLElement {
  connectedCallback() {
    if (this.dataset.initialized === 'true') return;
    this.dataset.initialized = 'true';
    this.images = Array.from(this.querySelectorAll('.ke-discovery__image'));
    this.current = this.querySelector('[data-ke-discovery-current]');
    this.index = Math.max(0, this.images.findIndex((image) => image.classList.contains('is-active')));
    this.hotspots = Array.from(this.querySelectorAll('.ke-hotspot'));
    this.querySelector('[data-ke-discovery-prev]')?.addEventListener('click', () => this.select(this.index - 1));
    this.querySelector('[data-ke-discovery-next]')?.addEventListener('click', () => this.select(this.index + 1));
    this.cleanupLayout = keObserveLayout(
      [this, ...this.hotspots.map((hotspot) => hotspot.querySelector('.ke-hotspot__copy')).filter(Boolean)],
      () => this.updateLayout()
    );
  }

  disconnectedCallback() {
    this.cleanupLayout?.();
  }

  updateLayout() {
    const discoveryRect = this.getBoundingClientRect();
    const mobileLayout = discoveryRect.width <= 749;
    const safePadding = 16;

    this.hotspots.forEach((hotspot) => {
      hotspot.classList.remove('ke-hotspot--force-left', 'ke-hotspot--force-right');

      const pinRect = hotspot.querySelector('.ke-hotspot__pin')?.getBoundingClientRect();
      if (!pinRect) return;

      const hotspotWidth = hotspot.getBoundingClientRect().width;
      const pinCenter = pinRect.left + (pinRect.width / 2) - discoveryRect.left;
      const footprint = hotspotWidth - (pinRect.width / 2);
      const leftFits = pinCenter - footprint >= safePadding;
      const rightFits = pinCenter + footprint <= discoveryRect.width - safePadding;
      const prefersRight = mobileLayout
        ? hotspot.classList.contains('ke-hotspot--mobile-right')
        : hotspot.classList.contains('ke-hotspot--right');

      if (prefersRight && !rightFits && leftFits) {
        hotspot.classList.add('ke-hotspot--force-left');
      } else if (!prefersRight && !leftFits && rightFits) {
        hotspot.classList.add('ke-hotspot--force-right');
      } else if (!leftFits && !rightFits) {
        hotspot.classList.add(pinCenter >= discoveryRect.width / 2 ? 'ke-hotspot--force-left' : 'ke-hotspot--force-right');
      }
    });
  }

  select(index) {
    if (!this.images.length) return;
    this.index = (index + this.images.length) % this.images.length;
    this.images.forEach((image, imageIndex) => image.classList.toggle('is-active', imageIndex === this.index));
    if (this.current) this.current.textContent = String(this.index + 1).padStart(2, '0');
  }
}

if (!customElements.get('kabluchka-editorial-discovery')) {
  customElements.define('kabluchka-editorial-discovery', KabluchkaEditorialDiscovery);
}

document.addEventListener('shopify:block:select', (event) => {
  const slide = event.target.closest('[data-ke-slide]');
  const hero = event.target.closest('kabluchka-editorial-hero');
  if (!slide || !hero) return;
  hero.select(hero.slides.indexOf(slide), true);
});
